import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Send, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { BRANDS } from "@/i18n/translations";
import { Reveal, KineticLines } from "@/components/motion/Reveal";

const EMPTY = { name: "", company: "", email: "", phone: "", country: "", brand: "", volume: "", application: "", message: "" };

function Field({ label, name, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-inkmute font-semibold">{label}{required && " *"}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-2.5 text-ink placeholder:text-inkmute/50 transition-colors"
      />
    </label>
  );
}

export default function Contact() {
  const { t, loc, lang } = useLang();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("type") === "export" ? "export" : "general");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (params.get("type") === "export") setTab("export");
  }, [params]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, inquiry_type: tab };
      const { data } = await api.post("/inquiries", payload);
      toast.success(data.message || t("contact.success"));
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const isExport = tab === "export";

  return (
    <div data-testid="contact-page">
      <section className="pt-36 md:pt-48 pb-12 max-w-[1440px] mx-auto px-6 md:px-12">
        <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] text-ink">
          <KineticLines lines={[t("contact.title")]} />
        </h1>
        <p className="mt-8 max-w-2xl text-inkmute text-lg leading-relaxed">{t("contact.sub")}</p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* FORM */}
        <div className="lg:col-span-7">
          {/* Tabs */}
          <div className="flex gap-2 mb-10 border-b border-line" data-testid="contact-tabs">
            {[
              { k: "general", label: t("contact.tabGeneral") },
              { k: "export", label: t("contact.tabExport") },
            ].map((tb) => (
              <button
                key={tb.k}
                data-testid={`tab-${tb.k}`}
                onClick={() => setTab(tb.k)}
                className={`relative px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${tab === tb.k ? "text-ink" : "text-inkmute hover:text-ink"}`}
              >
                {tb.label}
                {tab === tb.k && <span className="absolute bottom-[-1px] left-0 w-full h-0.5 bg-claris" />}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-7" data-testid="inquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
              <Field label={t("contact.name")} name="name" value={form.name} onChange={onChange} required testid="field-name" />
              <Field label={t("contact.company")} name="company" value={form.company} onChange={onChange} testid="field-company" />
              <Field label={t("contact.email")} name="email" type="email" value={form.email} onChange={onChange} required testid="field-email" />
              <Field label={t("contact.phone")} name="phone" value={form.phone} onChange={onChange} testid="field-phone" />
              {isExport && <Field label={t("contact.country")} name="country" value={form.country} onChange={onChange} testid="field-country" />}
              {isExport && <Field label={t("contact.volume")} name="volume" value={form.volume} onChange={onChange} testid="field-volume" />}
            </div>

            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-inkmute font-semibold">{t("contact.brand")}</span>
              <select
                name="brand"
                value={form.brand}
                onChange={onChange}
                data-testid="field-brand"
                className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-2.5 text-ink transition-colors"
              >
                <option value="">—</option>
                {(isExport ? BRANDS.filter((b) => b.slug === "clara") : BRANDS).map((b) => (
                  <option key={b.slug} value={b.name}>{b.name} — {loc(b.tagline)}</option>
                ))}
              </select>
            </label>

            {isExport && <Field label={t("contact.appField")} name="application" value={form.application} onChange={onChange} testid="field-application" placeholder={lang === "id" ? "Bakery, minuman, dairy…" : "Bakery, beverages, dairy…"} />}

            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-inkmute font-semibold">{t("contact.message")} *</span>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={4}
                data-testid="field-message"
                className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-2.5 text-ink resize-none transition-colors"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="inquiry-submit-btn"
              className="group inline-flex items-center gap-2 bg-ink text-cream rounded-full pl-8 pr-6 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-claris transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? t("contact.sending") : t("contact.send")}
              <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </form>
        </div>

        {/* INFO */}
        <div className="lg:col-span-5">
          <Reveal className="bg-white border border-line p-8 md:p-10">
            <div className="text-[11px] uppercase tracking-[0.25em] text-inkmute font-semibold mb-6">{t("contact.reach")}</div>
            <ul className="space-y-6 text-ink">
              <li className="flex gap-4"><MapPin size={20} className="text-claris shrink-0 mt-0.5" /><span>Jl. Raya Narogong, Pangkalan II, Cikiwul – Bantargebang, Bekasi, Jawa Barat, Indonesia 17152</span></li>
              <li className="flex gap-4"><Phone size={20} className="text-claris shrink-0" /><span>+62 21 8261 0031<br />+62 21 8251 113</span></li>
              <li className="flex gap-4"><Mail size={20} className="text-claris shrink-0" /><a href="mailto:marketing@tanputratama.co.id" className="hover:text-claris transition-colors">marketing@tanputratama.co.id</a></li>
              <li className="flex gap-4"><Clock size={20} className="text-claris shrink-0" /><span>{t("contact.hours")}<br /><span className="text-inkmute">{t("contact.hoursVal")}</span></span></li>
              <li className="flex gap-4"><Globe size={20} className="text-claris shrink-0" /><a href="https://www.claracocoa.co.id" target="_blank" rel="noreferrer" data-testid="contact-clara-web" className="hover:text-claris transition-colors">www.claracocoa.co.id</a></li>
            </ul>
          </Reveal>

          <div className="mt-6 border border-line overflow-hidden" data-testid="contact-map">
            <iframe
              title="PT Tan Putra Tama location"
              src="https://www.google.com/maps?q=Jl.+Raya+Narogong,+Cikiwul,+Bantargebang,+Bekasi&output=embed"
              className="w-full h-[320px] grayscale-[0.2]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
